import React, { useState, useEffect, useMemo, useRef } from "react"
import { useParams } from "react-router-dom"
import dayjs from "dayjs"

// components
import { Flex } from "antd"
import GridTypeA from "components/grids/GridTypeA/GridTypeA"
import { PrimeButton, StatusBox } from "components-design-system"
import PrimeField from "components/PrimeField/PrimeField"
import CreateActivityModal from "./subcomponents/CreateActivityModal"
import EditWorkflowModal from "components/WorkflowTracker/EditWorkflowModal/EditWorkflowModal"
import ActivitiesTimeline from "components/charts/ActivitiesTimeline/ActivitiesTimeline"
import { Panel } from "components-design-system"

// apis
import {
  useGetStudyActivities,
  usePostStudyActivity,
  useGetStudyActivitiesTimeline,
} from "api/hooks/studies/useActivitiesApi"
import { useUserPermissions } from "api/hooks"
import { useGetStudy } from "api/hooks/studies/useStudiesApi"
import { useGetActivityTypes } from "api/hooks/activities/useActivitiesApi"
import { useGetStudyTeam } from "api/hooks/studies/studyTeamsApi"
import { useGetStudyHeader } from "api/hooks/studies/useStudiesApi"
import { useGetWorkflow } from "api/hooks/workflows/useWorkflowsApi"

// context
import useWorkflowTracker from "context/hooks/useWorkflowTracker"
import useActivitiesColDefs from "../../hooks/useActivitiesColDefs"

// styles
import "./Activities.scss"

const Activities = () => {
  const { studyId } = useParams()
  const { data: study } = useGetStudy(studyId)
  const { data: activities = [] } = useGetStudyActivities(studyId)
  const { data: permissions = {} } = useUserPermissions(studyId)
  const { data: team } = useGetStudyTeam(studyId)
  const { data: headerData } = useGetStudyHeader(studyId)
  const { data: activityTypes = [] } = useGetActivityTypes()
  const { data: timeline = [] } = useGetStudyActivitiesTimeline(studyId)
  const postStudyActivity = usePostStudyActivity(studyId)
  const [isNewActivityLoading, setIsNewActivityLoadingLoading] = useState(false)
  const [selectedFilters, setSelectedFilters] = useState(["Not at Risk", "At Risk", "Late"])
  const onTime = useRef(0)
  const atRisk = useRef(0)
  const late = useRef(0)
  const gridRef = useRef()
  const [filters, setFilters] = useState({
    hidePreviousActivities: true,
    hideCancelledActivities: true,
    hideCompletedAndFutureActivities: false,
  })

  const { columnDefs } = useActivitiesColDefs(studyId)

  const formattedTimeline = useMemo(() => {
    let res = []

    if (Array.isArray(timeline) && study) {
      timeline.forEach((activity) => {
        res.push({
          project_id: activity.id,
          project_title: activity.project_title,
          study_db_id: studyId,
          study_id: study.study_id,
          start_date: dayjs(activity.start_date).format("YYYY-MM-DD"),
          end_date: dayjs(activity.end_date).format("YYYY-MM-DD"),
          status: activity.status,
          milestones: [
            {
              title: "Compliance Due Date",
              date: activity.milestones.compliance_due_date,
            },
            {
              title: "Activity Due Date",
              date: activity.milestones.project_due_date,
            },
          ],
        })
      })
    }

    return res
  }, [timeline])

  const [openCreateActivity, setOpenCreateActivity] = useState(false)
  const [newActivityIds, setNewActivityIds] = useState(null)

  const { workflowTrackerData, setWorkflowTrackerData, setWorkflowId, setWorkflowModel, setOpenEditStatesPanel } =
    useWorkflowTracker()

  const { data: workflowTrackerDataResp, isLoading: isWorkflowLoading } = useGetWorkflow({
    workflowId: newActivityIds?.workflowId,
    workflowModel: "activity",
  })

  useEffect(() => {
    if (workflowTrackerDataResp && isWorkflowLoading === false) {
      setWorkflowId(newActivityIds?.workflowId)
      setWorkflowModel("activity")
      setWorkflowTrackerData({
        ...workflowTrackerDataResp,
        state_data: {
          ...workflowTrackerDataResp.state_data,
          tracker_data: workflowTrackerDataResp.state_data.tracker_data.map((state) => {
            return {
              ...state,
              start_state_date: state?.start_state_date?.split("T")?.[0],
              end_state_date: state?.end_state_date?.split("T")?.[0],
            }
          }),
        },
      })
      setOpenEditStatesPanel(true)
    }
  }, [isWorkflowLoading])

  const onChangeFilters = (key, value) => {
    setFilters({
      ...filters,
      [key]: value,
    })
  }

  const keys = ["conception_date", "pra", "fsa", "fpi", "lpo", "pcd", "fcsr"]
  const labels = {
    conception_date: "Conception date",
    fcsr: "CSR Completed",
  }

  const milestones = useMemo(() => {
    if (headerData) {
      return keys.map((key) => {
        let milestone = headerData[key]

        return {
          key,
          label: labels[key] ? labels[key] : key.toUpperCase(),
          values: {
            ...milestone,
          },
        }
      })
    }
    return []
  }, [headerData])

  const filteredRowData = useMemo(() => {
    if (Array.isArray(activities)) {
      if (!filters.hideCancelledActivities) {
        gridRef?.current?.api?.setRowData(activities)
        gridRef?.current?.api?.setFilterModel({
          curr_state: { filterType: "set", values: ["Cancelled"] },
        })
      }

      const filteredData =
        activities
          ?.map((activity) => ({ ...activity, study_id: Number(studyId) }))
          .filter((activity) => {
            if (selectedFilters.length > 0 && activity.curr_state === "Cancelled") {
              return false
            } else if (
              filters.hideCompletedAndFutureActivities &&
              (activity.actual_completion_date || activity.progress_status === "planned")
            ) {
              return false
            }
            return true
          }) || []

      onTime.current = 0
      atRisk.current = 0
      late.current = 0

      filteredData.forEach((activity) => {
        if (activity.status === "Not at Risk" && activity.curr_state !== "Cancelled") onTime.current++
        else if (activity.status === "At Risk" && activity.curr_state !== "Cancelled") atRisk.current++
        else if (activity.status === "Late" && activity.curr_state !== "Cancelled") late.current++
      })

      return filteredData
    }

    return []
  }, [activities, filters])

  const onCreateActivity = (formData) => {
    let request = {
      ...formData,
    }

    request.project_type_id = activityTypes.find((item) => item.title === request.project_type_id).id

    if (!request.comment_text || request.comment_text === "") delete request.comment_text
    setIsNewActivityLoadingLoading(true)
    postStudyActivity(
      { body: request },
      {
        onSuccess: (resp) => {
          setNewActivityIds({
            activityId: resp.data.project_id,
            workflowId: resp.data.workflow_id,
          })
          setOpenCreateActivity(false)
        },
        onSettled: () => {
          setIsNewActivityLoadingLoading(false)
        },
      }
    )
  }

  const actions = React.useMemo(() => {
    const items = []

    if (permissions?.["project.create"] === true) {
      items.push(
        <PrimeButton
          type={"add"}
          onClick={() => setOpenCreateActivity(true)}
        >
          Create Activity
        </PrimeButton>
      )
    }

    return items
  }, [permissions])

  const handleStatusBox = (status) => {
    const gridApi = gridRef.current.api
    const currentFilters = selectedFilters || []

    const newFilters = currentFilters.includes(status)
      ? currentFilters.filter((filter) => filter !== status)
      : [...currentFilters, status]

    setSelectedFilters(newFilters)

    if (gridApi) {
      gridApi.setFilterModel({
        status: { values: newFilters },
      })
    }
    setFilters({ ...filters, hideCancelledActivities: true })
  }

  const onFilterChanged = ({ api, source }) => {
    if (source === "columnFilter") {
      const filterInstance = api.getFilterInstance("status")
      const stepFilterInstance = api.getFilterInstance("curr_state")

      if (!stepFilterInstance.getModel()) {
        setFilters({ ...filters, hideCancelledActivities: true })
      } else if (
        stepFilterInstance.getModel()?.values.includes("Cancelled") === false &&
        !filters.hideCompletedAndFutureActivities
      ) {
        setFilters({ ...filters, hideCancelledActivities: true })
      } else if (stepFilterInstance.getModel()?.values.includes("Cancelled")) {
        setFilters({ ...filters, hideCancelledActivities: false })
      }

      const filterValues = filterInstance.getModel()?.values || null
      const filterModel = api.getFilterModel()

      if (!stepFilterInstance.getModel() && !filterValues) {
        setSelectedFilters(["Not at Risk", "At Risk", "Late"])
      } else if (!stepFilterInstance.getModel() && filterValues) {
        setSelectedFilters(filterValues)
      }
      api.setFilterModel(filterModel)
    }
  }

  return (
    <Flex
      vertical={true}
      gap={24}
      className="study-activities"
    >
      <Panel
        title="ACTIVITIES"
        iconClassName="fa-light fa-arrow-progress"
        subtitle={
          <p className="study-activities-subtitle">
            All activities for {study?.study_id} in Prime and historical activities from CTG.
          </p>
        }
        actions={actions}
      >
        <Flex vertical={true}>
          <div className="study-activities-panel">
            <div className="study-activities-status">
              <StatusBox
                name={"On Time"}
                value={onTime.current}
                status="success"
                onClick={() => handleStatusBox("Not at Risk")}
                disabled={onTime.current === 0}
                active={selectedFilters.includes("Not at Risk") && onTime.current > 0}
              />
              <StatusBox
                name={"At Risk"}
                value={atRisk.current}
                status="warning"
                onClick={() => handleStatusBox("At Risk")}
                disabled={atRisk.current === 0}
                active={selectedFilters.includes("At Risk") && atRisk.current > 0}
              />
              <StatusBox
                name={"Late"}
                value={late.current}
                status="error"
                onClick={() => handleStatusBox("Late")}
                disabled={late.current === 0}
                active={selectedFilters.includes("Late") && late.current > 0}
              />

              <div className="study-activities-status-checkboxes">
                <PrimeField
                  schema={{
                    type: "checkbox",
                    label: "Hide Previous Activities Pulled from CTG  (not in Prime)",
                  }}
                  value={filters.hidePreviousActivities}
                  onChange={(val) => {
                    onChangeFilters("hidePreviousActivities", val)
                  }}
                />
                <PrimeField
                  schema={{
                    type: "checkbox",
                    label: "Hide Cancelled Activities",
                  }}
                  value={filters.hideCancelledActivities}
                  onChange={(val) => {
                    onChangeFilters("hideCancelledActivities", val)
                    if (!val) {
                      setSelectedFilters([])
                    } else {
                      setSelectedFilters(["Late", "Not at Risk", "At Risk"])
                      gridRef?.current?.api?.setRowData(activities)
                      gridRef?.current?.api?.setFilterModel({
                        status: { filterType: "set", values: ["Late", "Not at Risk", "At Risk"] },
                      })
                    }
                  }}
                />
                <PrimeField
                  schema={{
                    type: "checkbox",
                    label: "Hide Completed and Future Activities",
                  }}
                  value={filters.hideCompletedAndFutureActivities}
                  onChange={(val) => onChangeFilters("hideCompletedAndFutureActivities", val)}
                />
              </div>
            </div>
          </div>
          <GridTypeA
            gridRef={gridRef}
            rowData={filteredRowData}
            onFilterChanged={onFilterChanged}
            columnDefs={columnDefs}
            domLayout="autoHeight"
            pagination={true}
            paginationPageSize={10}
            paginationPageSizeSelector={[10, 20, 50, 100]}
          />
        </Flex>
      </Panel>
      <Panel
        title="TIMELINE"
        iconClassName="fa-light fa-timeline"
        subtitle={<p>All activities for {study?.study_id} visually represented along a timeline</p>}
      >
        <ActivitiesTimeline
          milestones={milestones}
          timeline={formattedTimeline}
        />
        <CreateActivityModal
          open={openCreateActivity}
          onCreateActivity={onCreateActivity}
          isLoading={isNewActivityLoading}
          onCancel={() => setOpenCreateActivity(false)}
          activities={activities}
        />
        {newActivityIds && workflowTrackerData && (
          <EditWorkflowModal
            data={{
              workflow_id: newActivityIds?.workflowId,
              activity_id: newActivityIds?.activityId,
              study_uid: study.study_id,
            }}
            team={team}
            type="study"
          />
        )}
      </Panel>
    </Flex>
  )
}

export default Activities
